import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, UserCircle, SignOut, ClockCounterClockwise, Truck } from "@phosphor-icons/react";
import api, { formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  awaiting_payment: "border-amber-400/40 text-amber-300",
  paid: "border-cyan/40 text-cyan",
  processing: "border-cyan/40 text-cyan",
  shipped: "border-emerald-400/40 text-emerald-300",
  delivered: "border-emerald-400/40 text-emerald-300",
  cancelled: "border-destructive/40 text-destructive",
};

function StatusBadge({ status }) {
  return (
    <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[status] || "border-teal/40 text-teal"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/orders/my")
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="account-page" className="mx-auto max-w-5xl px-6 py-14 sm:px-8 lg:px-12">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-cyan/15 pb-8">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center border border-cyan/30 bg-navy-surface text-cyan">
            <UserCircle size={30} weight="duotone" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight text-white">{user?.name}</h1>
            <p className="font-mono text-xs text-teal-muted">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Link to="/admin" className="border border-cyan/30 px-4 py-2 font-mono text-xs uppercase tracking-wider text-cyan hover:bg-cyan/10">
              Admin
            </Link>
          )}
          <button
            data-testid="account-logout"
            onClick={logout}
            className="flex items-center gap-2 border border-cyan/15 px-4 py-2 font-mono text-xs uppercase tracking-wider text-teal-muted transition-colors hover:border-destructive/40 hover:text-destructive"
          >
            <SignOut size={15} /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="mb-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cyan">
          <ClockCounterClockwise size={16} /> Order History
        </h2>

        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`order-skeleton-${i}`} className="h-28 animate-pulse border border-cyan/10 bg-navy-surface" />
            ))}
          </div>
        )}
        {!loading && orders.length === 0 && (
          <div className="border border-cyan/15 bg-navy-surface p-14 text-center" data-testid="no-orders">
            <Package size={48} weight="duotone" className="mx-auto text-cyan/50" />
            <p className="mt-4 font-mono text-sm uppercase tracking-wider text-teal-muted">No orders yet</p>
            <Link to="/shop" className="mt-6 inline-block bg-cyan px-6 py-2.5 font-mono text-xs font-semibold uppercase tracking-wider text-navy-deep hover:bg-cyan-bright">
              Start Shopping
            </Link>
          </div>
        )}
        {!loading && orders.length > 0 && (
          <div className="space-y-5">
            {orders.map((order) => (
              <div key={order.id} data-testid={`order-${order.id}`} className="border border-cyan/15 bg-navy-surface p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-cyan/10 pb-4">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-teal">Order</span>
                    <p className="font-mono text-sm text-white">#{order.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-teal">
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                    <div className="mt-1"><StatusBadge status={order.status} /></div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {order.items.map((item, idx) => (
                    <div key={`${item.product_id}-${idx}`} className="flex justify-between font-mono text-sm">
                      <span className="text-teal-muted">
                        {item.quantity} × {item.name}
                      </span>
                      <span className="text-white">{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                {order.discount > 0 && (
                  <div className="mt-3 flex justify-between font-mono text-xs text-cyan">
                    <span>Discount ({order.coupon_code})</span>
                    <span>- {formatCurrency(order.discount)}</span>
                  </div>
                )}
                <div className="mt-4 flex justify-between border-t border-cyan/10 pt-4">
                  <span className="font-mono text-xs uppercase tracking-widest text-white">Total</span>
                  <span className="font-mono text-lg font-bold text-cyan">{formatCurrency(order.total)}</span>
                </div>
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`track-shipment-${order.id}`}
                    className="mt-4 flex items-center gap-2 border-l-2 border-cyan bg-navy px-4 py-3 font-mono text-xs uppercase tracking-wider text-white transition-colors hover:bg-navy-elevated"
                  >
                    <Truck size={16} className="text-cyan" />
                    Track Shipment
                    <span className="ml-auto text-[11px] text-teal">
                      {order.tracking_carrier?.toUpperCase()} · {order.tracking_number}
                    </span>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
